import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import LegalLinks, { GoogleConsentNotice, SignupConsentCheckbox } from './LegalLinks';
import { useLanguage } from '../i18n';

const GoogleIcon = () => (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        />
        <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        />
        <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        />
        <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        />
    </svg>
);

const SignupPage = ({ onSignup, onGoogleLogin }) => {
    const { t } = useLanguage();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    // Unchecked by default and never pre-ticked — a pre-ticked box is not
    // consent.
    const [acceptedLegal, setAcceptedLegal] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Belt and braces: the disabled button is the UI, this is the contract.
        // A form can still be submitted around a disabled button (Enter in a
        // field, an automated client), and an account must never be created
        // without the box having been ticked.
        if (!acceptedLegal) {
            setError(t('authLegalConsentRequired', 'Please accept the Terms and Conditions to create an account.'));
            return;
        }

        if (password !== confirmPassword) {
            setError(t('auxSignupPasswordMismatch', 'Passwords do not match'));
            return;
        }

        if (password.length < 6) {
            setError(t('auxSignupPasswordTooShort', 'Password must be at least 6 characters long'));
            return;
        }

        setLoading(true);

        try {
            const result = await onSignup(username, email, password);
            if (result.success) {
                // Firebase automatically signs in after signup; a fresh sign-in
                // always lands on the Main Page (#512).
                navigate('/home');
            } else {
                setError(result.error || t('auxSignupFailed', 'Signup failed. Please try again.'));
            }
        } catch (err) {
            setError(t('auxUnexpectedError', 'An unexpected error occurred. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        setError('');
        setGoogleLoading(true);

        try {
            const result = await onGoogleLogin();
            if (result.success) {
                navigate('/home');
            } else {
                setError(result.error || t('auxSignupGoogleFailed', 'Google sign up failed.'));
            }
        } catch (err) {
            setError(t('auxUnexpectedError', 'An unexpected error occurred. Please try again.'));
        } finally {
            setGoogleLoading(false);
        }
    };

    // The account cannot be created until the consent box is ticked.
    const canSubmit = acceptedLegal && !loading && !googleLoading;

    return (
        <div className="relative flex h-dvh overflow-y-auto bg-pai-void text-white">
            <div
                aria-hidden="true"
                className="fixed inset-0 bg-cover bg-center"
                style={{ backgroundImage: "url('/wallpaper.jpg')" }}
            />
            <div aria-hidden="true" className="fixed inset-0 bg-[rgba(5,7,10,0.64)]" />
            {/* #432: `m-auto` centres the card while it fits and lets the page
                scroll when it does not (a phone held sideways, a short window).
                `items-center` on the parent used to centre it OUTSIDE the
                viewport instead, cutting off the top and the submit button. */}
            <div className="relative w-full max-w-md px-6 py-6 m-auto">
            <div className="w-full p-8 space-y-6 rounded-modal border border-white/[0.14] bg-white/[0.03] backdrop-blur-tile">
                <h2 className="text-[28px] font-extrabold tracking-[-0.025em] text-center text-white">{t('auxSignUp', 'Sign up')}</h2>

                {error && (
                    <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
                        {error}
                    </div>
                )}

                {/* Google Sign-Up Button */}
                <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={googleLoading || loading}
                    className="flex items-center justify-center w-full h-11 px-4 text-sm font-semibold text-white border border-pai bg-white/[0.05] rounded-ctl hover:bg-white/[0.1] hover:border-pai-strong transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {googleLoading ? (
                        <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {t('auxSignupSigningUp', 'Signing up...')}
                        </span>
                    ) : (
                        <>
                            <GoogleIcon />
                            <span className="ml-2">{t('auxLoginContinueGoogle', 'Continue with Google')}</span>
                        </>
                    )}
                </button>

                {/* Google sign-up creates the account in one tap and never
                    reaches the form's consent box, so the terms are stated at
                    the point of that tap instead. */}
                <GoogleConsentNotice className="-mt-3" />

                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-pai-subtle" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-2 text-pai-meta bg-transparent">{t('auxSignupOrEmail', 'Or sign up with email')}</span>
                    </div>
                </div>

                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div>
                        <label htmlFor="username" className="block text-sm font-medium text-pai-secondary">
                            {t('auxUsername', 'Username')}
                        </label>
                        <input
                            id="username"
                            name="username"
                            type="text"
                            autoComplete="username"
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="block w-full h-11 px-3 mt-1 pai-input appearance-none focus:outline-none text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-pai-secondary">
                            {t('auxEmailAddress', 'Email address')}
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="block w-full h-11 px-3 mt-1 pai-input appearance-none focus:outline-none text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-pai-secondary">
                            {t('commonPassword', 'Password')}
                        </label>
                        <div className="relative mt-1">
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                autoComplete="new-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full h-11 px-3 pr-10 pai-input appearance-none focus:outline-none text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/45 hover:text-white transition-colors"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-pai-secondary">
                            {t('auxConfirmPassword', 'Confirm Password')}
                        </label>
                        <div className="relative mt-1">
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                autoComplete="new-password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="block w-full h-11 px-3 pr-10 pai-input appearance-none focus:outline-none text-sm"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-white/45 hover:text-white transition-colors"
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {password && confirmPassword && password !== confirmPassword && (
                            <p className="mt-1 text-sm text-red-600">{t('auxSignupPasswordMismatch', 'Passwords do not match')}</p>
                        )}
                    </div>
                    {/* Explicit, affirmative consent. The text is assembled
                        from i18n parts so each language places its own article
                        beside the right link instead of embedding markup in a
                        translated string. */}
                    <SignupConsentCheckbox checked={acceptedLegal} onChange={setAcceptedLegal} />
                    <button
                        type="submit"
                        disabled={!canSubmit}
                        className={`w-full h-11 px-4 text-sm font-semibold rounded-ctl transition-colors focus:outline-none ${
                            canSubmit
                                ? 'bg-white text-pai-void hover:bg-white/90'
                                : 'bg-white/40 text-pai-void cursor-not-allowed'
                        }`}
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('auxSignupCreatingAccount', 'Creating account...')}
                            </span>
                        ) : (
                            t('auxSignupSubmit', 'Sign up with Email')
                        )}
                    </button>
                </form>

                <p className="text-sm text-center text-pai-meta">
                    {t('auxSignupHaveAccount', 'Already have an account?')}{' '}
                    <Link
                        to="/login"
                        className="font-semibold text-pai-blue hover:text-pai-blue-tint"
                    >
                        {t('auxLogIn', 'Log in')}
                    </Link>
                </p>
            </div>
            <LegalLinks className="mt-6" />
            </div>
        </div>
    );
};

export default SignupPage;
